import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { HiArrowLongDown } from "react-icons/hi2";
import { Link } from "react-router-dom";

import SlideTransition from "../SlideTransition";

import hero_fallback_bg from "../../assets/background/hero_fallback_bg.png";
import video1 from "../../assets/videos/hero_video_1.mp4";
import video2 from "../../assets/videos/hero_video_2.mp4";
import video3 from "../../assets/videos/hero_video_3.mp4";
import video4 from "../../assets/videos/hero_video_4.mp4";
import video5 from "../../assets/videos/hero_video_5.mp4";

import useInView from "../../hooks/useInView";
import useContent from "../../hooks/useContent";
import { MAIN_ROUTES } from "../../utils/routes";

import { styles } from "./style";
import { theme } from "../../utils/theme";

// List of curated videos
const videoListHd = [video1, video2, video3, video4, video5];

const Hero = () => {
  const content = useContent();
  const [setRef, visible] = useInView({
    threshold: 0.1,
  });
  const matches = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const containerRef = useRef(null);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [mouseOver, setMouseOver] = useState(false);

  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videoListHd.length);
    }, 7000); // Change video every 7 seconds

    return () => clearInterval(videoInterval);
  }, [currentVideoIndex]);

  const handleOnClickRequest = () => {
    document
      .querySelector(`#${MAIN_ROUTES.get("PRODUCTS").ID}`)
      ?.scrollIntoView({
        behavior: "smooth",
      });
  };

  /** Function to render video background */
  const renderVideo = () => {
    // Return JSX with all videos. Only one video is visible at a time.
    return videoListHd.map((video, index) => {
      return (
        <video
          autoPlay
          id="_hero_video"
          key={index}
          loop
          muted
          playsInline
          poster={hero_fallback_bg}
          style={{
            ...styles.video,
            opacity: index === currentVideoIndex ? 1 : 0,
          }}
        >
          <source src={video} type="video/mp4" />
        </video>
      );
    });
  };

  const handleClickSelector = (index) => () => {
    setCurrentVideoIndex(index);
  };

  return (
    <Box id={MAIN_ROUTES.get("HOME").ID} ref={setRef} sx={styles.heroContainer}>
      <Container maxWidth="xl" ref={containerRef} sx={styles.heroForeground}>
        <Box sx={styles.heroBody}>
          <Box sx={styles.heroContent}>
            <Stack spacing={matches ? 4 : 5}>
              <SlideTransition appear={true} timeout={700}>
                <Typography sx={styles.heroText}>
                  {content.hero.content}
                </Typography>
              </SlideTransition>

              <SlideTransition appear={true} timeout={600}>
                <Box
                  sx={{
                    alignItems: "start",
                    display: "flex",
                    position: "relative",
                    minHeight: 120,
                  }}
                >
                  <Box
                    sx={{
                      alignItems: "start",
                      display: "flex",
                      flexDirection: "column",
                      gap: matches ? 2 : 4,
                    }}
                  >
                    {content.hero.headers.map((text, index) => (
                      <Typography
                        key={index}
                        sx={{
                          ...styles.heroHeader,
                          display:
                            index === currentVideoIndex ? "block" : "none",
                          opacity: index === currentVideoIndex ? 1 : 0,
                        }}
                      >
                        {text}
                      </Typography>
                    ))}

                    {content.hero.subheaders.map((text, index) => (
                      <Typography
                        key={index}
                        sx={{
                          ...styles.heroSubheader,
                          display:
                            index === currentVideoIndex ? "block" : "none",
                          opacity: index === currentVideoIndex ? 1 : 0,
                        }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </SlideTransition>
            </Stack>

            <Box sx={styles.heroAction}>
              {/* <SlideTransition appear={true} timeout={800}> */}
              <Button
                onClick={handleOnClickRequest}
                onMouseEnter={() => setMouseOver(true)}
                onMouseLeave={() => setMouseOver(false)}
                sx={styles.button}
                variant="contained"
              >
                {content.hero.action}

                <Box sx={{ display: "flex", position: "relative", width: 16 }}>
                  <HiArrowLongDown
                    size={"1.2em"}
                    style={{
                      position: "absolute",
                      transform: mouseOver
                        ? "translateY(-7px)"
                        : "translateY(-40px)",
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                  <HiArrowLongDown
                    size={"1.2em"}
                    style={{
                      position: "absolute",
                      transform: mouseOver
                        ? "translateY(40px)"
                        : "translateY(-7px)",
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                </Box>
              </Button>
              {/* </SlideTransition> */}

              <Button
                color="primary"
                component={Link}
                sx={styles.buttonSecondary}
                to={MAIN_ROUTES.get("CONTACT").PATH}
                variant="contained"
              >
                {content.hero.subaction}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>

      {renderVideo()}

      <Box
        sx={{
          alignItems: "center",
          bottom: 20,
          display: "flex",
          gap: 2,
          justifyContent: "center",
          position: "absolute",
          width: "100vw",
          zIndex: 99,
        }}
      >
        {videoListHd.map((ele, index) => (
          <Box
            key={index}
            onClick={handleClickSelector(index)}
            sx={{
              backgroundColor:
                currentVideoIndex === index
                  ? theme.palette.primary.main
                  : theme.palette.grays.white,
              borderRadius: 8,
              cursor: "pointer",
              height: 8,
              transition: "all 0.3s ease-in-out",
              width: currentVideoIndex === index ? 48 : 14,
            }}
          ></Box>
        ))}
      </Box>
    </Box>
  );
};

export default Hero;
